import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { supabase } from '@/integrations/supabase/client';
import { getGivenName } from '@/lib/displayName';
import { useOnboarding } from './OnboardingContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import meddLogo from '@/assets/medd-logo.png';
import { 
  Users, 
  Building2, 
  Palette, 
  Shield,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  ArrowRight,
  Sparkles,
  Crown,
  Settings,
  BarChart3,
  GraduationCap,
  ExternalLink,
  PartyPopper
} from 'lucide-react';

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  completed: boolean;
  action?: {
    label: string;
    path: string;
  };
}

// Master tenant ID - admins of this tenant should NEVER see onboarding wizard
const MASTER_TENANT_ID = '00000000-0000-0000-0000-000000000001';

const INITIAL_STEPS: Omit<OnboardingStep, 'completed'>[] = [
  {
    id: 'welcome',
    title: 'Welcome to your Admin Dashboard',
    description: 'You\'re now the Super Admin for your organization. This dashboard gives you complete control over your training platform.',
    icon: Crown,
  },
  {
    id: 'role',
    title: 'Your Role as Super Admin',
    description: 'As Super Admin, you can manage users, configure simulations, view analytics, and customize your platform branding.',
    icon: Shield,
  },
  {
    id: 'team',
    title: 'Add Your Team Members',
    description: 'Start by inviting your team members. You can assign different roles like managers, trainers, or regular users.',
    icon: Users,
    action: {
      label: 'Go to User Management',
      path: '/admin/users',
    },
  },
  {
    id: 'structure',
    title: 'Create Branches/Departments',
    description: 'Organize your team by creating companies or departments. This helps with reporting and permissions.',
    icon: Building2,
    action: {
      label: 'Manage Companies',
      path: '/admin/companies',
    },
  },
  {
    id: 'branding',
    title: 'Customize Your Branding',
    description: 'Make the platform your own! Upload your logo, set brand colors, and configure your organization\'s appearance.',
    icon: Palette,
    action: {
      label: 'Open Settings',
      path: '/settings',
    },
  },
];

const STORAGE_KEY = 'superadmin_onboarding_progress';

export function SuperAdminOnboardingWizard() {
  const { user, hasRole } = useAuth();
  const { tenant, isMasterTenant } = useTenant();
  const { showOnboarding } = useOnboarding();
  const navigate = useNavigate();
  
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);

  // Use tenant branding
  const platformName = tenant.tenant_name || 'MEDD-SIM';
  const logoUrl = !isMasterTenant && tenant.logo_url ? tenant.logo_url : meddLogo;
  const primaryColor = tenant.primary_color || '#233C6F';

  // Check if user is a super admin (platform_admin)
  const isSuperAdmin = user?.user_type === 'platform_admin' || hasRole('admin');

  // Check if current tenant (from URL) is master tenant
  // Also check if user's profile is from master tenant
  // Onboarding wizard is only for white-label tenant admins, not master MEDD admins
  const isUserFromMasterTenant = isMasterTenant || user?.tenant_id === MASTER_TENANT_ID || !user?.tenant_id;

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.id || !isSuperAdmin) return;

      // The general first-login welcome owns this moment. Recheck the shared
      // completion flag after it closes instead of stacking two dialogs.
      if (showOnboarding) {
        setIsOpen(false);
        return;
      }

      // BUG FIX #1: Don't show wizard to master MEDD tenant admins
      // Only white-label tenant super admins should see this wizard
      if (isUserFromMasterTenant) {
        return;
      }

      try {
        // Check if onboarding is completed in DB
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();

        // If onboarding already completed, don't show
        if (profile?.onboarding_completed) {
          return;
        }

        // Check localStorage for progress
        const savedProgress = localStorage.getItem(`${STORAGE_KEY}_${user.id}`);
        let initialSteps = INITIAL_STEPS.map(step => ({ ...step, completed: false }));
        
        if (savedProgress) {
          try {
            const parsed = JSON.parse(savedProgress);
            initialSteps = INITIAL_STEPS.map(step => ({
              ...step,
              completed: parsed.completedSteps?.includes(step.id) || false,
            }));
            setCurrentStep(parsed.currentStep || 0);
          } catch (e) {
            console.error('Error parsing onboarding progress:', e);
          }
        }

        setSteps(initialSteps);
        setIsOpen(true);
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };

    checkOnboardingStatus();
  }, [user?.id, isSuperAdmin, isUserFromMasterTenant, showOnboarding]);

  // Save progress to localStorage
  useEffect(() => {
    if (!user?.id || steps.length === 0) return;
    
    const progress = {
      currentStep,
      completedSteps: steps.filter(s => s.completed).map(s => s.id),
    };
    localStorage.setItem(`${STORAGE_KEY}_${user.id}`, JSON.stringify(progress));
  }, [currentStep, steps, user?.id]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      // Mark current step as completed
      const updatedSteps = [...steps];
      updatedSteps[currentStep].completed = true;
      setSteps(updatedSteps);
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setIsOpen(false);
  };

  const handleAction = (path: string) => {
    // Mark step as completed
    const updatedSteps = [...steps];
    updatedSteps[currentStep].completed = true;
    setSteps(updatedSteps);
    
    setIsOpen(false);
    navigate(path);
  };

  const handleComplete = async () => {
    setIsCompleting(true);
    
    try {
      // Mark final step as completed
      const updatedSteps = [...steps];
      updatedSteps[currentStep].completed = true;
      setSteps(updatedSteps);

      // Save to database
      if (user?.id) {
        await supabase
          .from('profiles')
          .update({ 
            onboarding_completed: true 
          })
          .eq('id', user.id);
        
        // Clear localStorage
        localStorage.removeItem(`${STORAGE_KEY}_${user.id}`);
      }

    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsCompleting(false);
      setIsOpen(false);
    }
  };

  const currentStepData = steps[currentStep];
  const progress = steps.length > 0 
    ? ((steps.filter(s => s.completed).length / steps.length) * 100)
    : 0;
  const isLastStep = currentStep === steps.length - 1;

  // Don't render for master tenant users, non-admins, or when wizard is closed
  if (!isOpen || !isSuperAdmin || steps.length === 0 || isUserFromMasterTenant) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden">
        <DialogTitle className="sr-only">Super Admin Onboarding</DialogTitle>
        <DialogDescription className="sr-only">Complete the onboarding steps to set up your platform</DialogDescription>
        {/* Header with gradient */}
        <div 
          className="p-6 text-white relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <img 
                src={logoUrl} 
                alt={platformName} 
                className="h-8 object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = meddLogo;
                }}
              />
              <Badge className="bg-white/20 text-white border-0 hover:bg-white/30">
                <Crown className="h-3 w-3 mr-1" />
                Super Admin Setup
              </Badge>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center">
                {currentStepData && <currentStepData.icon className="h-7 w-7" />}
              </div>
              <div>
                <p className="text-white/70 text-sm font-medium">
                  Step {currentStep + 1} of {steps.length}
                </p>
                <h2 className="text-xl font-bold">
                  {currentStepData?.title}
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 pt-4">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium transition-all
                  ${step.completed 
                    ? 'bg-green-100 text-green-600' 
                    : index === currentStep 
                      ? 'bg-primary text-white' 
                      : 'bg-muted text-muted-foreground'
                  }`}
              >
                {step.completed ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 0: Welcome - BUG FIX #2: Tenant-branded welcome */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 rounded-xl p-5 border border-purple-100 dark:border-purple-900">
                <div className="flex items-start gap-4">
                  {/* Show tenant logo instead of generic icon */}
                  <div className="h-14 w-14 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0 p-2">
                    <img 
                      src={logoUrl} 
                      alt={platformName} 
                      className="h-full w-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = meddLogo;
                      }}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground mb-1">
                      Welcome to {platformName}! 🎉
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Hi {getGivenName(user?.name, 'Admin')}, you're now the Super Admin for <strong>{platformName}</strong>. This dashboard gives you complete control over your training platform.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg border bg-card">
                  <Users className="h-6 w-6 text-primary mb-2" />
                  <p className="font-medium text-sm">Manage Users</p>
                  <p className="text-xs text-muted-foreground">Add and manage your team</p>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <BarChart3 className="h-6 w-6 text-primary mb-2" />
                  <p className="font-medium text-sm">View Analytics</p>
                  <p className="text-xs text-muted-foreground">Track team performance</p>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <GraduationCap className="h-6 w-6 text-primary mb-2" />
                  <p className="font-medium text-sm">Configure Training</p>
                  <p className="text-xs text-muted-foreground">Set up simulations</p>
                </div>
                <div className="p-4 rounded-lg border bg-card">
                  <Settings className="h-6 w-6 text-primary mb-2" />
                  <p className="font-medium text-sm">Platform Settings</p>
                  <p className="text-xs text-muted-foreground">Customize everything</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Role explanation */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <p className="text-muted-foreground">{currentStepData?.description}</p>
              
              <div className="space-y-3">
                {[
                  { icon: Users, title: 'User Management', desc: 'Add, edit, and manage all team members and their permissions' },
                  { icon: GraduationCap, title: 'Content Control', desc: 'Configure AI simulations and learning pathways' },
                  { icon: BarChart3, title: 'Analytics & Reports', desc: 'Access comprehensive training metrics and performance data' },
                  { icon: Palette, title: 'Branding & Settings', desc: 'Customize your platform\'s look and feel' },
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{item.title}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2+: Action steps */}
          {currentStep >= 2 && (
            <div className="space-y-4">
              <p className="text-muted-foreground">{currentStepData?.description}</p>
              
              {currentStepData?.action && (
                <Button 
                  className="w-full"
                  size="lg"
                  onClick={() => handleAction(currentStepData.action!.path)}
                >
                  {currentStepData.action.label}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              )}

              <p className="text-center text-xs text-muted-foreground">
                You can always do this later from the navigation menu
              </p>
            </div>
          )}

          {/* Final step completion */}
          {isLastStep && (
            <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800 dark:text-green-200">You're all set!</p>
                  <p className="text-sm text-green-600 dark:text-green-400">
                    Complete this step or finish setup to start using your admin dashboard.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button variant="ghost" size="sm" onClick={handlePrev}>
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Back
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={handleSkip}>
                Skip for now
              </Button>
            </div>
            
            {isLastStep ? (
              <Button onClick={handleComplete} disabled={isCompleting}>
                {isCompleting ? 'Completing...' : 'Finish Setup'}
                <CheckCircle2 className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={handleNext}>
                Next
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
